/**
 * Raw design tokens — the atomic color, spacing, and typography values.
 * Semantic tokens and theme.css reference these.
 */

export const primitives = {
  // ============================================
  // COLORS
  // ============================================
  white: "#ffffff",
  // Near-white base canvas / card fill — effectively neutral white, a hair off
  // pure #ffffff, kept just lighter than the warm neutral ramp's lightest stop
  // (surface-contrast = primaryWarm.0) so cards read as raised against the
  // tinted page canvas. Remaps --coval-white in theme.css (the global default).
  whiteWarm: "#FEFEFE",
  black: "#000000",
  transparent: "transparent",

  primary: {
    0: "#FAFAFA",
    50: "#f3f3f3",
    100: "#EDEDED",
    200: "#d4d4d4",
    300: "#a3a3a3",
    400: "#858585",
    500: "#6b6b6b",
    600: "#525252",
    700: "#3a3a3a",
    800: "#262626",
    900: "#171717",
  },

  // Warm greige neutral ramp — the product's neutral grays, anchored on
  // #DBDBD3 (the 200 stop). Same lightness progression as the legacy cool
  // `primary` ramp but with a warm yellow/tan undertone (R ≥ G > B). theme.css
  // remaps --coval-primary-* to these app-wide (the global default); both light
  // and dark themes reference --coval-primary-*, so this re-tones everything.
  // The cool `primary` ramp above is retained only as the remap fallback.
  primaryWarm: {
    0: "#FBFAF6",
    50: "#F3F2EB",
    100: "#EAE9E0",
    200: "#DBDBD3",
    // Half steps sized to WCAG minimums. 250 is light surface-2-hover and dark
    // muted text, 425 is the lightest light-theme control outline that holds
    // 3:1 on surface-1, and 650 is the dark surface-1-hover and surface-2
    // fill.
    250: "#CECBC2",
    300: "#ADA99B",
    400: "#8C887B",
    425: "#89847E",
    500: "#716E62",
    600: "#57544A",
    650: "#4F4C44",
    700: "#3D3B34",
    800: "#292720",
    900: "#0f0c0a",
  },

  gray: {
    0: "#f9fafb",
    50: "#f0f0f0",
    100: "#d9d9d9",
    300: "#a1a1a1",
    600: "#757575",
    900: "#4d4d4d",
  },

  red: {
    100: "#faebeb",
    200: "#FFDCDC",
    600: "#EE6666",
    700: "#E83838",
    900: "#AE1414",
  },

  // Brand accent triads. Each fills three semantic slots: 100 = brand *light*
  // (surface), 600 = a deeper, saturated mid (border),
  // 900 = brand *dark* (text). 200 holds brand *mid* as an optional stronger
  // surface (and drives the stage tokens). Brand ships no red — the `red` ramp
  // above is the derived danger accent and is not part of the brand triads.
  blue: {
    100: "#e6ecfb",
    200: "#c6dcfa",
    600: "#435b71",
    900: "#1a2c36",
  },

  green: {
    100: "#ecf6d5",
    200: "#dfeac2",
    600: "#437367",
    900: "#12322b",
  },

  orange: {
    100: "#f9e5d2",
    200: "#f5cea2",
    600: "#755b44",
    900: "#321f16",
  },

  purple: {
    100: "#f0e8f4",
    200: "#dac4e1",
    600: "#764b66",
    900: "#40141b",
  },

  // The transcript's agent side — the blue its chat bubble has always been,
  // and now the blue the lane its audio is drawn in takes too. Distinct from
  // the `blue` brand triad, which is a desaturated slate.
  sky: {
    100: "#e0f2fe",
    200: "#bae6fd",
    800: "#075985",
    900: "#0c4a6e",
  },

  amber: {
    100: "#fef3e0",
    400: "#fbbf24",
    500: "#f59e0b",
    600: "#f9a825",
    900: "#e65100",
    950: "#451a03",
  },

  // ============================================
  // TYPOGRAPHY
  // ============================================
  fontFamily: {
    sans: "'Inter', sans-serif",
  },

  fontSize: {
    xs: "11px",
    sm: "12px",
    base: "14px",
  },

  fontWeight: {
    normal: "400",
    medium: "500",
    semibold: "600",
  },

  lineHeight: {
    tight: "1.3",
    normal: "1.5",
  },

  // ============================================
  // SPACING (4px base unit)
  // ============================================
  spacing: {
    0: "0px",
    0.5: "2px",
    1: "4px",
    1.5: "6px",
    2: "8px",
    3: "12px",
    4: "16px",
    6: "24px",
    8: "32px",
    9: "36px",
  },

  // ============================================
  // SIZING
  // ============================================
  size: {
    3: "12px",
    4: "16px",
    6: "24px",
    8: "32px",
    10: "40px",
  },

  // ============================================
  // BORDER RADIUS
  // ============================================
  radius: {
    none: "0px",
    sm: "2px",
    md: "4px",
    lg: "8px",
    xl: "16px",
    full: "9999px",
  },

  // ============================================
  // BORDER WIDTH
  // ============================================
  borderWidth: {
    0: "0px",
    1: "1px",
    2: "2px",
  },

  // ============================================
  // TRANSITIONS
  // ============================================
  duration: {
    fast: "150ms",
    normal: "200ms",
    slow: "300ms",
  },

  // ============================================
  // OPACITY
  // ============================================
  opacity: {
    disabled: "0.5",
  },
} as const
